import { supabase } from "../../supabase/supabase";

const callServiceUrl = () => import.meta.env.VITE_CALL_SERVICE_URL || "";

export type PublicTestLink = {
  id: string;
  token: string;
  assistant_id: string;
  label: string | null;
  expires_at: string;
  revoked_at: string | null;
  session_count: number;
  last_used_at: string | null;
  created_at: string;
  status: "active" | "expired" | "revoked";
};

export type PublicTestLinkInfo = {
  assistant_name: string;
  expires_at: string;
  /** Prompt variables the visitor can fill in (system-filled ones are already left out). */
  variables: string[];
};

export type PublicTestSession = {
  call_id: string;
  room_name: string;
  v2_ws_url: string;
};

/** The URL a visitor opens. Built from the current origin so it works on every environment. */
export const publicTestLinkUrl = (token: string) =>
  `${window.location.origin}/test/${token}`;

/** Error carrying the HTTP status, so the public page can tell expired (410) from unknown (404). */
export class PublicTestLinkError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
  }
}

async function readError(response: Response): Promise<PublicTestLinkError> {
  let message = `Request failed (${response.status})`;
  try {
    const body = await response.json();
    if (typeof body?.detail === "string") message = body.detail;
  } catch {
    // Not JSON - keep the generic message
  }
  return new PublicTestLinkError(message, response.status);
}

async function memberFetch(path: string, init: RequestInit = {}) {
  const { data } = await supabase.auth.getSession();
  const accessToken = data.session?.access_token;
  if (!accessToken) {
    throw new PublicTestLinkError("Authentication required. Please log in again.", 401);
  }
  const response = await fetch(`${callServiceUrl()}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
      ...init.headers,
    },
  });
  if (!response.ok) throw await readError(response);
  return response.json();
}

export async function createPublicTestLink(params: {
  assistantId: string;
  expiresInHours: number;
  label?: string;
}): Promise<PublicTestLink> {
  return memberFetch("/api/public-test-links", {
    method: "POST",
    body: JSON.stringify({
      assistant_id: params.assistantId,
      expires_in_hours: params.expiresInHours,
      ...(params.label ? { label: params.label } : {}),
    }),
  });
}

export async function listPublicTestLinks(
  assistantId: string,
): Promise<PublicTestLink[]> {
  const result = await memberFetch(
    `/api/public-test-links?assistant_id=${encodeURIComponent(assistantId)}`,
  );
  return result.data;
}

export async function revokePublicTestLink(linkId: string): Promise<PublicTestLink> {
  return memberFetch(`/api/public-test-links/${linkId}`, { method: "DELETE" });
}

// The two calls below are for visitors with no Voxi login: no auth header.

export async function getPublicTestLinkInfo(
  token: string,
): Promise<PublicTestLinkInfo> {
  const response = await fetch(
    `${callServiceUrl()}/api/public/test-links/${encodeURIComponent(token)}`,
  );
  if (!response.ok) throw await readError(response);
  return response.json();
}

export async function startPublicTestSession(
  token: string,
  visitorName?: string,
  variables?: Record<string, string>,
): Promise<PublicTestSession> {
  const response = await fetch(
    `${callServiceUrl()}/api/public/test-links/${encodeURIComponent(token)}/session`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...(visitorName ? { visitor_name: visitorName } : {}),
        ...(variables && Object.keys(variables).length > 0 ? { variables } : {}),
      }),
    },
  );
  if (!response.ok) throw await readError(response);
  return response.json();
}
